<template>
	<AppBottomSheet :active="showInstantPayoutBottomSheet" @toggling-popup="toggleInstantPayoutBottomSheet" :force-width="instantPaymentBottomSheetWidth">
		<ModalHeader :title="getString('stripe_instant_payout')" :button-text="getString('stripe_instant_payout_proceed')"
					 @clickButton="sendInstantPayoutRequest" :button-loading="processInstantPayout"/>
		<div class="row-dutch">
			<div class="dutch-container">
				<label class="label-style mb-5">{{getString('balance')}}</label>
				<div class="dutch-value">{{ readableInstantAvailableAmount }}</div>
			</div>
			<div class="dutch-container">
				<div class="label-spacing mb-5">
					<label class="label-style">{{getString('stripe_instant_payout_proceed')}}</label>
				</div>
				<div class="dutch-value dutch-value-clickable">
					<input ref="amount-to-payout" type="number" inputmode="decimal" class="dutch-value-input" :value="currencyStore.dbToActualValue(amountToPayout)"
						   @keyup="updateAmountToPayout" @focusout="checkAmountValidity">
					<span class="dutch-currency-style">{{currencyStore.currCurrency.symbol}}</span>
				</div>
			</div>
			<div class="fee-description">
				<p>{{getString('stripe_instant_payout_fee_description')}}</p>
			</div>
		</div>
	</AppBottomSheet>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import {useLanguageStore, useNotificationStore, useCurrencyStore} from "@/shared/stores";
import API from "@/shared/helpers/API";
import {formatDateMMDDHHmm} from "@/shared/helpers/dates";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useUserStore} from "@/console-new/stores";

export default {
	setup() {
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {currencyStore, getString: useLanguageStore().getString,  showNotification: useNotificationStore().showNotification, userStore};
	},
	props: {
		showInstantPayoutBottomSheet: {
			type: Boolean,
			default: false
		},
		instantAvailableAmount: {
			type: Number,
			defaults: 0
		},
		instantPayoutDestination: {
			type: String,
			default: null
		}
	},
	mixins: [WindowWidth],
	data() {
		return {
			amountToPayout: 0,
			newInstantAvailableAmount: null,
			processInstantPayout: false,
		}
	},
	emits: ['toggling-popup'],
	name: "StripeInstantPayoutBottomSheet" ,
	components: {AppBottomSheet, ModalHeader},
	computed: {
		min() {
			return this.currencyStore.currCurrency.min_instant_payout_value;
		},
		max() {
			return this.currencyStore.currCurrency.max_instant_payout_value;
		},
		instantPaymentBottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else {
				return "550px";
			}
		},
		readableInstantAvailableAmount() {
			let amount = this.newInstantAvailableAmount ? this.newInstantAvailableAmount : this.instantAvailableAmount;
			return this.currencyStore.readableCurrencyValue(amount);
		},
		readableMinValue() {
			return this.currencyStore.readableCurrencyValue(this.min);
		},
		readableMaxValue() {
			return this.currencyStore.readableCurrencyValue(this.max);
		}
	},
	methods: {
		toggleInstantPayoutBottomSheet(active) {
			if(!active) {
				this.$emit('toggling-popup')
			}
		},
		sendInstantPayoutRequest() {
            this.processInstantPayout = true;

			let availableAmount = this.newInstantAvailableAmount ? this.newInstantAvailableAmount : this.instantAvailableAmount;
			if(this.amountToPayout < this.min || this.amountToPayout > this.max || this.amountToPayout > availableAmount) {
                this.processInstantPayout = false;
				this.showNotification('stripe-instant-payout-invalid-amount', [this.readableMinValue, this.readableMaxValue]);
				this.amountToPayout = availableAmount > this.max ? this.max : availableAmount;
				return;
			}

			API.init().instantPayout(this.userStore.currBusinessId, this.amountToPayout, this.instantPayoutDestination, (res) => {
				this.processInstantPayout = false;
				if(res.result === 'OK'){

					if(this.newInstantAvailableAmount) {
						this.newInstantAvailableAmount -= this.amountToPayout;
					} else {
						this.newInstantAvailableAmount = this.instantAvailableAmount - this.amountToPayout;
					}
					this.amountToPayout = this.newInstantAvailableAmount > this.max ? this.max : this.newInstantAvailableAmount;
					let fundsDateArrival = formatDateMMDDHHmm(new Date(res.arrival_date * 1000));
					this.showNotification('stripe-instant-payout-success',[fundsDateArrival]);
				} else {
					this.amountToPayout = this.instantAvailableAmount;
					this.showNotification('stripe-instant-payout-error');
				}
			});
		},
		updateAmountToPayout() {
			this.amountToPayout = this.currencyStore.actualToDbValue(this.$refs['amount-to-payout'].value);
		},
		checkAmountValidity() {
            setTimeout(() => {
                if(this.processInstantPayout) {
                    return;
                }

                let invalidAmount = false;
                if(this.amountToPayout < this.min) {
                    this.amountToPayout = this.min;
                    invalidAmount = true;
                }
                else if(this.amountToPayout > this.instantAvailableAmount && this.instantAvailableAmount > this.max) {
                    this.amountToPayout = this.max;
                    invalidAmount = true;
                }
                else if(this.amountToPayout > this.max) {
                    this.amountToPayout = this.max;
                    invalidAmount = true;
                }

                if(invalidAmount) {
                    this.showNotification('stripe-instant-payout-invalid-amount', [this.readableMinValue, this.readableMaxValue]);
                }
            }, 100)
		}
	},
	watch: {
		instantAvailableAmount: function(newValue, oldValue) {
			if(newValue > 0) {
				this.amountToPayout = newValue > this.max ? this.max : newValue;
			}
		}
	},
}
</script>

<style scoped lang="scss">

.row-dutch {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: flex-end;
	width: 100%;
	--dutch-gap: 20px;
	gap: var(--dutch-gap);
	flex-wrap: wrap;

	@media (max-width: 480px) {
		--dutch-gap: 12px;
	}

	@media (max-width: 370px) {
		--dutch-gap: 8px;
	}

	@media (max-width: 500px) {
		flex-direction: column;
		align-items: center;
	}

	.dutch-container {
		width: 100%;
		@media (min-width: 500px) {
			max-width: calc(50% - 10px);
			flex:0 0 calc(50% - 10px);
		}

		@mixin dutch-value-style {
			font-size: 22px;
			font-weight: 500;

			@media (max-width: 450px) {
				font-size: 18px;
			}

			@media (max-width: 390px) {
				font-size: 16px;
			}
		}

		@media (max-width: 360px) {
			width: 100%;
		}

		.dutch-value {
			text-align: right;
			padding: 8px 12px;
			border-radius: 13px;
			box-shadow: inset 1px 2px 4px var(--color-alpha-3);
			background-color: var(--color-alpha-1);
			height: 50px;
			display: flex;
			align-items: center;
			justify-content: flex-end;
			@include dutch-value-style;

			&.dutch-value-clickable {
				border: 1px solid var(--color-primary);
			}

			.dutch-value-input {
				background-color: transparent;
				border: 0;
				color: var(--color-txt);
				width: 100%;
				text-align: end;
				padding-right: 5px;
				@include dutch-value-style;
				&::-webkit-outer-spin-button,
				&::-webkit-inner-spin-button {
					-webkit-appearance: none;
					margin: 0;
				}
			}
			input[type=number]{
				-moz-appearance: textfield;
			}

			.dutch-currency-style {
				font-size: 22px;
				font-weight: 500;
				@media (max-width: 450px) {
					font-size: 18px;
				}
				@media (max-width: 390px) {
					font-size: 16px;
				}
			}
		}
	}

	.fee-description {
		display: flex;
		justify-content: flex-start;
		width: 100%;

		p {
			color: var(--txt-1);
			font-size: 10px;
		}
	}

	.label-style {
		display: flex;
		@include noSelect;
		color: var(--txt-1);
		font-size: 10px;
		line-height: 15px;
	}
	.label-spacing {
		display: flex;
		justify-content: space-between;
	}
	.payout-explain {
		display: flex;
		justify-content: flex-end;
	}

}
</style>